import type { CSSProperties } from "react";
import {
  SITE_EASE,
  SITE_LINE_HEIGHT,
  SITE_MEDIA,
  SITE_TYPE,
} from "src/domains/shared/constants/styles/site";
import { truncateNumber } from "src/domains/shared/utils/truncateNumber";
import { REPLAY_ATTR } from "./motion/reveal";

/** 첫 화면 위 여백과 오른쪽 앱 타일 한 변 — 업데이트 소식·매뉴얼 첫 화면이 같은 값을 쓴다 */
export const SITE_HERO_TOP = "clamp(64px,12vw,176px)";
export const SITE_HERO_MARK_SIZE = "clamp(200px,22vw,300px)";
/**
 * 좁은 화면(900px 이하 세로)의 앱 타일 한 변과 오른쪽 자리 — 첫머리 오른쪽 위, 화면 끝에서 본문 여백만큼 안쪽.
 * 타일은 글 뒤 배경이라 제목 줄바꿈은 타일과 상관없이 그대로다
 */
export const SITE_HERO_COMPACT_MARK_SIZE = "clamp(100px,28vw,120px)";
export const SITE_HERO_COMPACT_MARK_RIGHT = "calc(var(--gutter) + 4px)";

/** 가로로 둔 터치 화면의 앱 타일 한 변 — 화면 높이에 맞춰(844×390 에서 140px) 첫머리 글 옆에 둔다 */
export const SITE_HERO_LANDSCAPE_MARK_SIZE = "clamp(120px,36svh,200px)";
/** 첫 화면 리드 문단 위 간격 */
export const SITE_HERO_LEAD_GAP = "28px";

/**
 * 첫 화면 글이 시작하는 높이(타일 자리 위 끝 기준) — 머리말·한 줄 제목·한 줄 리드 묶음이 앱 타일 높이 가운데에 오는 값을 글자 토큰으로
 * 계산했다. 업데이트 소식·매뉴얼이 같이 써서, 제목·리드가 언어에 따라 두 줄이 되거나 리드 대신 검색창이 와도 두 페이지 제목이 같은
 * 높이에서 시작한다(가운데 정렬이면 줄 수에 따라 제목이 올라간다).
 */
export const siteHeroTextTop = (markSize: string) =>
  `calc((${markSize} - (${SITE_LINE_HEIGHT} * ${SITE_TYPE.eyebrow.fontSize} + ${SITE_TYPE.eyebrow.marginBottom} + ${SITE_TYPE.h1.lineHeight} * ${SITE_TYPE.h1.fontSize} + ${SITE_HERO_LEAD_GAP} + ${SITE_TYPE.lead.lineHeight} * ${SITE_TYPE.lead.fontSize})) / 2)`;
export const SITE_HERO_TEXT_TOP = siteHeroTextTop(SITE_HERO_MARK_SIZE);

/**
 * 첫 화면 앱 타일의 바닥 그림자 — 움직이지 않는 타일 자리의 ::before(타일보다 먼저 그려 타일 뒤에 깔린다). 제자리에서 떠 있는
 * 타일 높이에 따라 크기만 바뀐다: 타일이 오르면 작게, 내려오면 크게. 그래서 타일의 떠 있기도 지연 없는 8초 ease-in-out 왕복이고
 * keyframes 가 from=위·to=아래여야 박자가 맞는다.
 * `shiftX` 는 그림자를 자리 가운데에서 옆으로 옮기는 값(자리 너비 대비, 음수는 왼쪽) — 페이지마다 타일 기울기에 맞춰 준다.
 */
export const siteHeroTileShadow = (shiftX: `${number}%`) =>
  ({
    "&::before": {
      content: '""',
      position: "absolute",
      left: `calc(8% + ${shiftX})`,
      width: "84%",
      // 바닥 높이: 타일이 가장 내려왔을 때 아래 끝(자리의 약 106%)보다 조금 아래에 그림자 가운데가 온다
      top: "103%",
      height: "14%",
      borderRadius: "50%",
      background:
        "radial-gradient(closest-side,rgba(39,81,163,.2),transparent)",
      // 움직임 줄이기로 멈춘 타일(왕복 가운데 높이)에 맞는 크기
      transform: "scale(.875)",
      animation: "siteHeroShadowFloat 8s ease-in-out infinite alternate",
    },
    "@keyframes siteHeroShadowFloat": {
      from: { transform: "scale(.75)" },
      to: { transform: "scale(1)" },
    },
    [SITE_MEDIA.reducedMotion]: { "&::before": { animation: "none" } },
  } as const);

/** 밝아지는 길이와 순서 한 칸의 늦춤(초) */
const ENTER_FADE_S = 0.6;
const ENTER_STEP_S = 0.08;

const lateEnterKeyframes = (order: number) => {
  const delay = order * ENTER_STEP_S;
  const holdPercent = truncateNumber((delay / (ENTER_FADE_S + delay)) * 100, 3);
  return { [`0%, ${holdPercent}%`]: { opacity: 0 }, to: { opacity: 1 } };
};

/** 순서별 등장 키프레임(사이트 전역 스타일에 들어간다) — 늦은 순서는 늦춤만큼 앞을 투명하게 둔다 */
export const SITE_HERO_ENTER_KEYFRAMES = {
  "@keyframes siteFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
  "@keyframes siteFadeIn1": lateEnterKeyframes(1),
  "@keyframes siteFadeIn2": lateEnterKeyframes(2),
};

/**
 * 첫 화면 글이 차례로 나타나는 등장(시안 `.phero-in` 의 떠오름에서 자리 이동을 뺀 것 — 제자리에서 밝아지기만 한다) —
 * 자식마다 `enterOrder(n)` 만큼 0.08초씩 늦게. 시안은 `:nth-child` 로 늦췄지만, 서버 렌더 때 스타일 태그가 형제로 끼어 순서가
 * 틀어지므로 순서를 직접 준다. 화면 밖으로 나갔다 돌아오면 `replayOnEnterRef` 가 단 `data-replay` 로 애니메이션을 껐다 켜서
 * 처음부터 다시 재생한다.
 *
 * 늦춤은 `animation-delay` 로 주지 않는다 — WebKit 에서 지연 중인 애니메이션은 메인 스레드가 바쁘면 시작하지 못해, 첫 로드에
 * 그 요소만 멈췄다가 한 번에 튀어나온다(실측). 키프레임 안에 두면 지연 없는 요소처럼 처음부터 메인 스레드 밖에서 돈다.
 */
export const SITE_HERO_ENTER_STYLES = {
  "& > *": {
    animationName: "var(--enter-name, siteFadeIn)",
    animationDuration: `calc(${ENTER_FADE_S}s + var(--enter, 0) * ${ENTER_STEP_S}s)`,
    animationTimingFunction: SITE_EASE.out,
    animationFillMode: "both",
  },
  [`&[${REPLAY_ATTR}] > *`]: { animation: "none" },
} as const;

export const enterOrder = (order: 0 | 1 | 2) =>
  ({
    "--enter": order,
    "--enter-name": order === 0 ? "siteFadeIn" : `siteFadeIn${order}`,
  } as CSSProperties);
